/* eslint-disable complexity */

import {
    Grid,
    Typography,
} from '@mui/material'
import get from 'lodash/get'

import useLabel from 'app/hooks/useLabel'
import Card from 'app/shared-components/Card'
import TextSingleline from 'app/shared-components/TextSingleline'
import Label from 'app/shared-components/Label'
import StatusHandler from 'app/shared-components/StatusHandler'
import CompanyDataSelector from 'app/shared-components/CompanySelector/CompanyDataSelector'
import ChipCheckMark from 'app/common/DomainObject/ComponentMapper/ChipCheckMark'
import EnumSelector from 'app/shared-components/EnumSelector'
import useEnumValues from 'app/hooks/useEnumValues'
import {
    IndustryType,
} from 'app/enums/label.enums'
import useGetCompany from '../hooks/useGetCompany'

import Link from './Link'
import BillingAddresses from './BillingAddresses/BillingAddresses'
import Addresses from './Addresses'
import ParentCompanySubsidiaries from './ParentCompanySubsidiaries'
import useStyles from './CompanyOverview.styles'

const CompanyOverview = ({
    id,
}: { id: number }) => {
    const {
        data,
        isDataReady,
        isFetching,
        isError,
        error,
    } = useGetCompany(id)
    const getAllowedValues = useEnumValues()
    const {
        classes,
    } = useStyles()
    const l = useLabel()

    const mainAddress = get(data, 'addresses', []).find((address) => {
        return address.isMainAddress
    })
    const mainAddressCountry = get(mainAddress, 'country.countryName', '')

    return (
        <StatusHandler
            isSuccess={isDataReady}
            isFetching={isFetching}
            isError={isError}
            error={error}
        >
            <div className={classes.container}>
                {data?.id ? (
                    <Grid
                        container
                        spacing={2}
                    >
                        <Grid
                            size={{
                                xs: 12, md: 12,
                            }}
                            className={classes.tableContainer}
                        >
                            <Card
                                title="Summary"
                                fullHeight
                            >
                                <Grid
                                    container
                                    spacing={2}
                                >
                                    <Grid
                                        size={{
                                            xs: 3, md: 3,
                                        }}
                                    >
                                        <TextSingleline
                                            title={l('Company.companyName')}
                                            value={data.companyName}
                                        />
                                    </Grid>
                                    <Grid
                                        size={{
                                            xs: 3, md: 3,
                                        }}
                                    >
                                        <Label
                                            title={l('Company.hasAccount')}
                                        />
                                        <div
                                            className={classes.chip}
                                        >
                                            <ChipCheckMark
                                                value={data.hasAccount}
                                                justifyIcon="flex-start"
                                            />
                                        </div>
                                    </Grid>
                                    <Grid
                                        size={{
                                            xs: 3, md: 3,
                                        }}
                                    >
                                        <div>
                                            <Label title={l('Company.website')} />
                                            {data?.website ? <Link website={data.website} /> : '-'}
                                        </div>
                                    </Grid>
                                    <Grid
                                        size={{
                                            xs: 3, md: 3,
                                        }}
                                    >
                                        <CompanyDataSelector
                                            title={l('Company.parentCompany')}
                                            value={data.parentCompany}
                                            disabled
                                        />
                                    </Grid>
                                    <Grid
                                        size={{
                                            xs: 9,
                                        }}
                                    />
                                    <Grid
                                        size={{
                                            xs: 3,
                                        }}
                                    >
                                        <EnumSelector
                                            name="industryType"
                                            title={l('Accounts.industryType')}
                                            value={data.industryType}
                                            allowedValues={getAllowedValues(IndustryType)}
                                            disabled
                                        />
                                    </Grid>
                                </Grid>
                            </Card>
                        </Grid>
                        {Boolean(data?.parentCompany?.id) && (
                            <ParentCompanySubsidiaries
                                id={data.parentCompany.id}
                            />
                        )}
                        <Addresses
                            data={data}
                        />
                        <BillingAddresses
                            data={data}
                        />
                        {mainAddress
                            && (
                                <Grid
                                    size={{
                                        xs: 12, md: 3,
                                    }}
                                >
                                    <Card
                                        title="Main Address"
                                        fullHeight
                                    >
                                        <div>
                                            <Label title="Main Address" />
                                            <Typography className={classes.text}>
                                                {mainAddress.addressName}

                                            </Typography>
                                            <Typography className={classes.text}>
                                                {mainAddress.addressLine1}
                                            </Typography>
                                            <Typography className={classes.text}>
                                                {`${mainAddress.zip} ${mainAddress.city}`}
                                            </Typography>
                                            <Typography className={classes.text}>
                                                {mainAddressCountry}
                                            </Typography>
                                        </div>
                                    </Card>
                                </Grid>
                            )}
                    </Grid>
                ) : null}
            </div>
        </StatusHandler>
    )
}

export default CompanyOverview
